import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import Layout from "../../components/Layout";
import { toast } from "sonner";
import { Save, Plus, Trash2, RotateCw, ChevronLeft, AlertTriangle, TrendingUp, Award } from "lucide-react";

const PRIZE_KINDS = [
  { v: "none", label: "Ingen premie" },
  { v: "chips", label: "Chips" },
  { v: "vip_days", label: "VIP-dager" },
];
const CAP_PERIODS = [
  { v: "", label: "Ingen cap" },
  { v: "per_contest", label: "Per konkurranse" },
  { v: "per_n_spins", label: "Per N spinn" },
];

const emptySegment = () => ({
  id: null,
  label: "Nytt segment",
  weight: 1,
  prize_kind: "none",
  prize_amount: 0,
  cap_count: null,
  cap_period: null,
  cap_n: null,
  color: "#888888",
});

function SegmentRow({ seg, onChange, onRemove }) {
  const isCapped = seg.cap_count != null;
  return (
    <tr className="border-b border-white/10" data-testid="vw-segment-row">
      <td className="p-2">
        <input
          data-testid="vw-seg-label"
          value={seg.label}
          onChange={(e) => onChange({ ...seg, label: e.target.value })}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm w-44 text-[var(--cream)]"
          maxLength={60}
        />
      </td>
      <td className="p-2">
        <input
          data-testid="vw-seg-weight"
          type="number"
          min={1}
          max={100}
          value={seg.weight}
          onChange={(e) => onChange({ ...seg, weight: parseInt(e.target.value || "0", 10) })}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm w-16 text-[var(--cream)] text-right"
        />
      </td>
      <td className="p-2">
        <select
          data-testid="vw-seg-kind"
          value={seg.prize_kind}
          onChange={(e) => onChange({ ...seg, prize_kind: e.target.value, prize_amount: e.target.value === "none" ? 0 : seg.prize_amount })}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm text-[var(--cream)]"
        >
          {PRIZE_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
      </td>
      <td className="p-2">
        <input
          data-testid="vw-seg-amount"
          type="number"
          min={0}
          value={seg.prize_amount || 0}
          disabled={seg.prize_kind === "none"}
          onChange={(e) => onChange({ ...seg, prize_amount: parseInt(e.target.value || "0", 10) })}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm w-20 text-[var(--cream)] text-right disabled:opacity-40"
        />
      </td>
      <td className="p-2">
        <select
          data-testid="vw-seg-cap-period"
          value={seg.cap_period || ""}
          onChange={(e) => {
            const v = e.target.value || null;
            onChange({
              ...seg,
              cap_period: v,
              cap_count: v ? (seg.cap_count || 1) : null,
              cap_n: v === "per_n_spins" ? (seg.cap_n || 1000) : null,
            });
          }}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm text-[var(--cream)]"
        >
          {CAP_PERIODS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
      </td>
      <td className="p-2">
        <input
          data-testid="vw-seg-cap-count"
          type="number"
          min={1}
          disabled={!isCapped}
          value={seg.cap_count || ""}
          onChange={(e) => onChange({ ...seg, cap_count: parseInt(e.target.value || "0", 10) || null })}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm w-16 text-[var(--cream)] text-right disabled:opacity-40"
        />
      </td>
      <td className="p-2">
        <input
          data-testid="vw-seg-cap-n"
          type="number"
          min={1}
          disabled={seg.cap_period !== "per_n_spins"}
          value={seg.cap_n || ""}
          onChange={(e) => onChange({ ...seg, cap_n: parseInt(e.target.value || "0", 10) || null })}
          className="bg-black/40 border border-white/20 px-2 py-1 text-sm w-20 text-[var(--cream)] text-right disabled:opacity-40"
        />
      </td>
      <td className="p-2">
        <input
          data-testid="vw-seg-color"
          type="color"
          value={seg.color || "#888888"}
          onChange={(e) => onChange({ ...seg, color: e.target.value })}
          className="bg-transparent border border-white/20 w-8 h-7 cursor-pointer"
        />
      </td>
      <td className="p-2 text-right">
        <button
          data-testid="vw-seg-remove"
          onClick={onRemove}
          className="text-red-400 hover:text-red-300 p-1"
          title="Fjern segment"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

export default function VoteWheelAdmin() {
  const [wheel, setWheel] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contestFilter, setContestFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        api.get("/admin/vote-wheel"),
        api.get(`/admin/vote-wheel/stats${contestFilter ? `?contest_id=${encodeURIComponent(contestFilter)}` : ""}`),
      ]);
      setWheel(w.data.wheel);
      setStats(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Klarte ikke å laste hjul");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (wheel) load(); /* eslint-disable-next-line */ }, [contestFilter]);

  const weightSum = useMemo(() => (wheel?.segments || []).reduce((a, s) => a + (parseInt(s.weight, 10) || 0), 0), [wheel]);
  const rateLimitedCount = useMemo(() => (wheel?.segments || []).filter(s => s.cap_count != null).length, [wheel]);

  const updateSegment = (idx, newSeg) => {
    setWheel(w => ({ ...w, segments: w.segments.map((s, i) => i === idx ? newSeg : s) }));
  };
  const addSegment = () => {
    if ((wheel?.segments || []).length >= 30) {
      toast.error("Maks 30 segmenter");
      return;
    }
    setWheel(w => ({ ...w, segments: [...w.segments, emptySegment()] }));
  };
  const removeSegment = (idx) => {
    if ((wheel?.segments || []).length <= 2) {
      toast.error("Minst 2 segmenter kreves");
      return;
    }
    setWheel(w => ({ ...w, segments: w.segments.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (weightSum !== 100) {
      toast.error(`Sum av vekter må være 100 (er ${weightSum})`);
      return;
    }
    if (rateLimitedCount > 5) {
      toast.error(`Maks 5 rate-limited segmenter (har ${rateLimitedCount})`);
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/admin/vote-wheel", {
        title: wheel.title,
        description: wheel.description,
        segments: wheel.segments,
      });
      setWheel(res.data.wheel);
      toast.success("Hjul lagret");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Klarte ikke å lagre");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !wheel) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-10 text-[var(--cream)]">Laster …</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8 text-[var(--cream)]" data-testid="vote-wheel-admin">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Link to="/admin/statistikk" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1">
              <ChevronLeft className="h-3 w-3" /> Tilbake til admin
            </Link>
            <h1 className="font-display text-3xl tracking-wide mt-2">Avstemnings-hjul</h1>
            <p className="text-sm text-white/60 mt-1">Hjul brukerne spinner etter å ha avgitt en stemme i en konkurranse.</p>
          </div>
          <button
            data-testid="vw-reload"
            onClick={load}
            className="px-3 py-1.5 text-xs border border-white/20 hover:border-white/40 inline-flex items-center gap-1"
          >
            <RotateCw className="h-3 w-3" /> Oppdater
          </button>
        </div>

        {/* Totals */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="bg-black/40 border border-white/10 p-4" data-testid="vw-total-spins">
              <div className="text-xs text-white/50">Totalt antall spinn</div>
              <div className="text-2xl font-display mt-1">{stats.totals.spins}</div>
            </div>
            <div className="bg-black/40 border border-white/10 p-4" data-testid="vw-pending-grants">
              <div className="text-xs text-white/50">Venter på spinn (ikke brukt)</div>
              <div className="text-2xl font-display mt-1">{stats.totals.grants_pending}</div>
            </div>
            <div className="bg-black/40 border border-white/10 p-4" data-testid="vw-chips-paid">
              <div className="text-xs text-white/50">Chips utbetalt</div>
              <div className="text-2xl font-display mt-1">{stats.totals.chips_paid}</div>
            </div>
            <div className="bg-black/40 border border-white/10 p-4" data-testid="vw-vip-paid">
              <div className="text-xs text-white/50">VIP-dager utbetalt</div>
              <div className="text-2xl font-display mt-1">{stats.totals.vip_days_paid}</div>
            </div>
          </div>
        )}

        {/* Tittel/beskrivelse */}
        <div className="bg-black/40 border border-white/10 p-5 mb-6">
          <h2 className="text-sm uppercase tracking-wider text-white/60 mb-3">Tittel & beskrivelse</h2>
          <input
            data-testid="vw-title"
            value={wheel.title || ""}
            onChange={(e) => setWheel(w => ({ ...w, title: e.target.value }))}
            className="bg-black/40 border border-white/20 px-3 py-2 text-sm w-full mb-2 text-[var(--cream)]"
            maxLength={80}
            placeholder="Tittel"
          />
          <textarea
            data-testid="vw-desc"
            value={wheel.description || ""}
            onChange={(e) => setWheel(w => ({ ...w, description: e.target.value }))}
            rows={2}
            className="bg-black/40 border border-white/20 px-3 py-2 text-sm w-full text-[var(--cream)]"
            maxLength={300}
            placeholder="Beskrivelse"
          />
        </div>

        {/* Segmenter */}
        <div className="bg-black/40 border border-white/10 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm uppercase tracking-wider text-white/60">
              Segmenter ({wheel.segments.length}/30) — vekt-sum:{" "}
              <span className={weightSum === 100 ? "text-emerald-400" : "text-red-400"}>{weightSum}/100</span>
              {" — rate-limited: "}
              <span className={rateLimitedCount <= 5 ? "text-white/80" : "text-red-400"}>{rateLimitedCount}/5</span>
            </h2>
            <button
              data-testid="vw-add-segment"
              onClick={addSegment}
              className="px-3 py-1.5 text-xs border border-white/20 hover:border-[var(--magenta)] inline-flex items-center gap-1"
            >
              <Plus className="h-3 w-3" /> Legg til segment
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/50 text-left border-b border-white/10">
                  <th className="p-2">Etikett</th>
                  <th className="p-2">Vekt %</th>
                  <th className="p-2">Premie</th>
                  <th className="p-2">Antall</th>
                  <th className="p-2">Cap-periode</th>
                  <th className="p-2">Cap-antall</th>
                  <th className="p-2">N spinn</th>
                  <th className="p-2">Farge</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {wheel.segments.map((seg, i) => (
                  <SegmentRow
                    key={seg.id || `new-${i}`}
                    seg={seg}
                    onChange={(s) => updateSegment(i, s)}
                    onRemove={() => removeSegment(i)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {weightSum !== 100 && (
            <div className="mt-3 text-xs text-amber-400 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Sum av vekter må være eksakt 100 før du kan lagre.
            </div>
          )}

          <div className="mt-4">
            <button
              data-testid="vw-save"
              onClick={save}
              disabled={saving || weightSum !== 100 || rateLimitedCount > 5}
              className="px-4 py-2 text-sm bg-[var(--magenta)] text-black font-meta hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
            >
              <Save className="h-3 w-3" /> {saving ? "Lagrer …" : "Lagre"}
            </button>
          </div>
        </div>

        {/* Stats pr segment */}
        {stats && (
          <div className="bg-black/40 border border-white/10 p-5 mb-6" data-testid="vw-stats-segments">
            <h2 className="text-sm uppercase tracking-wider text-white/60 mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Statistikk pr segment
            </h2>
            <div className="mb-3 flex items-center gap-2">
              <label className="text-xs text-white/60">Filtrer på konkurranse-ID:</label>
              <input
                data-testid="vw-stats-contest-filter"
                value={contestFilter}
                onChange={(e) => setContestFilter(e.target.value)}
                placeholder="Tom = alle"
                className="bg-black/40 border border-white/20 px-2 py-1 text-xs w-72 text-[var(--cream)]"
              />
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/50 text-left border-b border-white/10">
                  <th className="p-2">Segment</th>
                  <th className="p-2">Premie</th>
                  <th className="p-2 text-right">Vunnet</th>
                  <th className="p-2">Cap</th>
                  <th className="p-2 text-right">Igjen</th>
                  <th className="p-2">Vinnere (cap&apos;et)</th>
                </tr>
              </thead>
              <tbody>
                {stats.segments.map(s => (
                  <tr key={s.id} className="border-b border-white/5" data-testid="vw-stats-row">
                    <td className="p-2">{s.label}</td>
                    <td className="p-2 text-white/60">
                      {s.prize_kind === "chips" ? `${s.prize_amount} chips`
                        : s.prize_kind === "vip_days" ? `${s.prize_amount} VIP-dager`
                        : "—"}
                    </td>
                    <td className="p-2 text-right font-mono">{s.total_won}</td>
                    <td className="p-2 text-white/60">
                      {s.cap_count != null ? (
                        s.cap_period === "per_contest" ? `${s.cap_count}/konkurranse`
                          : `${s.cap_count}/${s.cap_n} spinn`
                      ) : "—"}
                    </td>
                    <td className="p-2 text-right font-mono">
                      {s.stock_remaining != null ? (
                        <span className={s.stock_remaining === 0 ? "text-red-400" : "text-emerald-400"}>
                          {s.stock_remaining}{s.cap_count != null ? `/${s.cap_count}` : ""}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="p-2 text-white/70">
                      {(s.winners || []).slice(0, 5).map(w => (
                        <span key={`${w.user_id}-${w.at}`} className="inline-block mr-2">
                          @{w.username}
                        </span>
                      ))}
                      {(s.winners || []).length > 5 && (
                        <span className="text-white/40">+{s.winners.length - 5} flere</span>
                      )}
                      {(!s.winners || s.winners.length === 0) && s.cap_count != null && (
                        <span className="text-white/30">Ingen vinnere ennå</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Spinn pr konkurranse */}
        {stats && stats.per_contest && stats.per_contest.length > 0 && (
          <div className="bg-black/40 border border-white/10 p-5" data-testid="vw-stats-contests">
            <h2 className="text-sm uppercase tracking-wider text-white/60 mb-3 flex items-center gap-2">
              <Award className="h-4 w-4" /> Spinn pr konkurranse
            </h2>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/50 text-left border-b border-white/10">
                  <th className="p-2">Konkurranse</th>
                  <th className="p-2 text-right">Spinn</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {stats.per_contest.map(r => (
                  <tr key={r.contest_id} className="border-b border-white/5">
                    <td className="p-2">{r.title}</td>
                    <td className="p-2 text-right font-mono">{r.spins}</td>
                    <td className="p-2 text-right">
                      <button
                        onClick={() => setContestFilter(r.contest_id)}
                        className="text-xs text-[var(--magenta)] hover:underline"
                      >
                        Vis kun denne
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
